// Utility for Developer Testing & Sandbox Mode
export const SANDBOX_STORAGE_PREFIX = 'sandbox_db_';
export const SANDBOX_FLAG_KEY = 'satala_dev_sandbox_mode';

export const isTestingModeActive = () => {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem(SANDBOX_FLAG_KEY) === 'true';
};

export const enableTestingMode = () => {
  if (typeof window === 'undefined') return;
  localStorage.setItem(SANDBOX_FLAG_KEY, 'true');
  
  // Pastikan ada akun dev di session agar tidak ter-logout
  const existingSession = localStorage.getItem('satala_session');
  if (!existingSession) {
    const defaultDev = {
      id: 'dev_sandbox_user',
      full_name: 'Developer Satala (Sandbox)',
      role: 'developer',
      position: 'DEVELOPER WEB',
      email: 'dev@satala.com',
      status: 'aktif',
      akses_khusus: { semua_akses: true }
    };
    localStorage.setItem('satala_session', JSON.stringify(defaultDev));
  }

  // Reload untuk apply perubahan client secara menyeluruh
  window.location.reload();
};

export const disableTestingMode = () => {
  if (typeof window === 'undefined') return;
  localStorage.setItem(SANDBOX_FLAG_KEY, 'false');
  window.location.reload();
};

export const resetTestingDatabase = () => {
  if (typeof window === 'undefined') return;
  const keysToRemove = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(SANDBOX_STORAGE_PREFIX)) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach(key => localStorage.removeItem(key));
  window.location.reload();
};

export const getTestingStorageStats = () => {
  if (typeof window === 'undefined') return { totalItems: 0, entities: {} };
  const stats = {};
  let totalItems = 0;
  
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(SANDBOX_STORAGE_PREFIX)) {
      const entityName = key.replace(SANDBOX_STORAGE_PREFIX, '');
      try {
        const items = JSON.parse(localStorage.getItem(key) || '[]');
        const activeCount = items.filter(it => !it.deleted_at).length;
        stats[entityName] = activeCount;
        totalItems += activeCount;
      } catch {
        stats[entityName] = 0;
      }
    }
  }
  return { totalItems, entities: stats };
};

// Seed realistic dummy data for testing purposes
export const seedSampleTestingData = (shouldReload = true) => {
  if (typeof window === 'undefined') return;

  const now = new Date().toISOString();

  // 1. Client Account (MEET ME AT THE ALTAR & Glow Botanica)
  const sampleClients = [
    {
      id: 'client_sbx_meetme',
      brand_name: 'MEET ME AT THE ALTAR',
      company_name: 'CV Meet Me At The Altar Indonesia',
      email: 'owner@meetmeatthealtar.com',
      pic: 'Fadli / Brand Owner',
      phone: '081234567890',
      status: 'verified',
      database_code: 'MMA01',
      credit_limit: 100000000,
      alamat: 'Jl. Pajajaran No. 88, Baranangsiang, Bogor Timur, Kota Bogor, Jawa Barat 16143',
      alamat_lengkap: 'Jl. Pajajaran No. 88, Baranangsiang, Bogor Timur, Kota Bogor, Jawa Barat 16143',
      kota: 'Bogor',
      provinsi: 'Jawa Barat',
      kodepos: '16143',
      created_date: now,
      updated_date: now
    },
    {
      id: 'client_sbx_01',
      brand_name: 'Glow Botanica Lab',
      company_name: 'PT Glow Botanica Indonesia',
      email: 'demo@glowbotanica.com',
      pic: 'Siti Rahma',
      phone: '081298765432',
      status: 'verified',
      database_code: 'GB01',
      credit_limit: 50000000,
      alamat: 'Jl. Raya Pajajaran Kav. 12, Bogor',
      alamat_lengkap: 'Jl. Raya Pajajaran Kav. 12, Bogor',
      kota: 'Bogor',
      provinsi: 'Jawa Barat',
      kodepos: '16128',
      created_date: now,
      updated_date: now
    }
  ];
  localStorage.setItem(`${SANDBOX_STORAGE_PREFIX}ClientAccount`, JSON.stringify(sampleClients));

  // 2. Packaging: Botol
  const sampleBotol = [
    {
      id: 'botol_sbx_01',
      nama: 'Botol Kaca Parfum 50ml Square Clear Luxe',
      ukuran_label_ml: 50,
      ukuran_aktual_ml: 50,
      vendor: 'PT Packaging Prima Indonesia',
      harga_rupiah: 6500,
      harga_jual: 8500,
      mata_uang: 'IDR',
      stok: 2500,
      status: 'aktif',
      catatan: 'Botol Kaca Bening Kotak Premium 50ml Drat FEA 15mm',
      catatan_teknis: 'Bahan kaca borosilikat tebal tegap, leher FEA 15mm crimp',
      created_date: now,
      updated_date: now
    },
    {
      id: 'botol_sbx_02',
      nama: 'Botol Dropper Serum 20ml Amber Luxe',
      ukuran_label_ml: 20,
      ukuran_aktual_ml: 20,
      vendor: 'PT Packaging Prima Indonesia',
      harga_rupiah: 4500,
      harga_jual: 6000,
      mata_uang: 'IDR',
      stok: 3000,
      status: 'aktif',
      catatan: 'Botol Amber UV Protection 20ml',
      created_date: now,
      updated_date: now
    }
  ];
  localStorage.setItem(`${SANDBOX_STORAGE_PREFIX}Botol`, JSON.stringify(sampleBotol));

  // 3. Packaging: Tutup
  const sampleTutup = [
    {
      id: 'tutup_sbx_01',
      nama: 'Cap Tutup Gold Metal Magnetic 50ml',
      vendor: 'PT Packaging Prima Indonesia',
      harga_rupiah: 2500,
      harga_jual: 3500,
      mata_uang: 'IDR',
      stok: 2500,
      status: 'aktif',
      catatan: 'Cap Magnetic Gold Mirror Finish 50ml',
      catatan_teknis: 'Inner plastik PP, outer aluminium gold electroplating mewah',
      created_date: now,
      updated_date: now
    },
    {
      id: 'tutup_sbx_02',
      nama: 'Cap Dropper Pipet Gold 20ml',
      vendor: 'PT Packaging Prima Indonesia',
      harga_rupiah: 1800,
      harga_jual: 2500,
      mata_uang: 'IDR',
      stok: 3000,
      status: 'aktif',
      catatan: 'Pipet Kaca Karet Putih Ring Gold 20ml',
      created_date: now,
      updated_date: now
    }
  ];
  localStorage.setItem(`${SANDBOX_STORAGE_PREFIX}Tutup`, JSON.stringify(sampleTutup));

  // 4. Packaging: Spray
  const sampleSpray = [
    {
      id: 'spray_sbx_01',
      nama: 'Crimp Spray Gold Mist FEA 15mm',
      vendor: 'PT Packaging Prima Indonesia',
      tipe_spray: 'Biasa',
      harga_rupiah: 3000,
      harga_jual: 4500,
      mata_uang: 'IDR',
      stok: 2500,
      status: 'aktif',
      catatan: 'Spray Crimp Gold Mist Halus (Output 0.08ml/spray)',
      catatan_teknis: 'Nozzle spray FEA 15mm collar gold presisi tinggi',
      created_date: now,
      updated_date: now
    }
  ];
  localStorage.setItem(`${SANDBOX_STORAGE_PREFIX}Spray`, JSON.stringify(sampleSpray));

  // 5. Item Tambahan (Box Kemasan, Stiker, Label)
  const sampleItemTambahan = [
    {
      id: 'item_sbx_box_01',
      nama: 'Hardbox Sliding Meet Me At The Altar 50ml',
      kategori: 'Box',
      brand: 'MEET ME AT THE ALTAR',
      vendor: 'Percetakan Kemasan Nusantara',
      harga_rupiah: 5500,
      harga_jual: 7500,
      mata_uang: 'IDR',
      stok: 2500,
      status: 'aktif',
      catatan: 'Hardbox rigid sliding tebal 2mm embos hot print gold foil',
      created_date: now,
      updated_date: now
    },
    {
      id: 'item_sbx_stiker_01',
      nama: 'Stiker Label Vinyl Gold Foil 50ml',
      kategori: 'Stiker',
      brand: 'MEET ME AT THE ALTAR',
      vendor: 'Percetakan Kemasan Nusantara',
      harga_rupiah: 1200,
      harga_jual: 1800,
      mata_uang: 'IDR',
      stok: 3000,
      status: 'aktif',
      catatan: 'Waterproof vinyl label gold foil front & back',
      created_date: now,
      updated_date: now
    }
  ];
  localStorage.setItem(`${SANDBOX_STORAGE_PREFIX}ItemTambahan`, JSON.stringify(sampleItemTambahan));

  // 6. Bahan Cair (Bibit Parfum, Alkohol & Pelarut)
  const sampleBahanCair = [
    {
      id: 'bahan_sbx_01',
      nama: 'Fragrance Oil Meet Me At The Altar (French Grade A+)',
      kategori: 'Bibit',
      vendor: 'PT Chemika Prima Sejahtera',
      grade: 'Exclusive',
      satuan: 'kg',
      stok: 50,
      harga_rupiah: 850000,
      harga_jual: 1150000,
      mata_uang: 'IDR',
      status: 'aktif',
      catatan: 'Top: Bergamot, Pink Pepper | Heart: Rose, White Floral | Base: Amberwood, Madagascar Vanilla',
      created_date: now,
      updated_date: now
    },
    {
      id: 'bahan_sbx_02',
      nama: 'Ethanol Absolute 96% Perfume Grade',
      kategori: 'Alkohol',
      vendor: 'PT Chemika Prima Sejahtera',
      grade: 'Cosmetic Grade',
      satuan: 'liter',
      stok: 200,
      harga_rupiah: 65000,
      harga_jual: 85000,
      mata_uang: 'IDR',
      status: 'aktif',
      catatan: 'Deodorized Ethanol kosmetik murni tanpa bau tajam menyengat',
      created_date: now,
      updated_date: now
    }
  ];
  localStorage.setItem(`${SANDBOX_STORAGE_PREFIX}BahanCair`, JSON.stringify(sampleBahanCair));

  // 7. Inventory Items (Format umum inventori)
  const sampleInventory = [
    {
      id: 'inv_sbx_01',
      item_name: 'Botol Kaca Parfum 50ml Square Clear Luxe',
      category: 'botol',
      total_stock: 2500,
      unit: 'pcs',
      vendor: 'PT Packaging Prima Indonesia',
      minimum_stock: 100,
      created_date: now
    },
    {
      id: 'inv_sbx_02',
      item_name: 'Cap Tutup Gold Metal Magnetic 50ml',
      category: 'tutup',
      total_stock: 2500,
      unit: 'pcs',
      vendor: 'PT Packaging Prima Indonesia',
      minimum_stock: 100,
      created_date: now
    },
    {
      id: 'inv_sbx_03',
      item_name: 'Crimp Spray Gold Mist FEA 15mm',
      category: 'spray',
      total_stock: 2500,
      unit: 'pcs',
      vendor: 'PT Packaging Prima Indonesia',
      minimum_stock: 100,
      created_date: now
    },
    {
      id: 'inv_sbx_04',
      item_name: 'Fragrance Oil Meet Me At The Altar (French Grade A+)',
      category: 'bibit',
      total_stock: 50,
      unit: 'kg',
      vendor: 'PT Chemika Prima Sejahtera',
      minimum_stock: 5,
      created_date: now
    }
  ];
  localStorage.setItem(`${SANDBOX_STORAGE_PREFIX}InventoryItem`, JSON.stringify(sampleInventory));

  // 8. Legalitas & Jasa
  const sampleLegalitas = [
    {
      id: 'leg_sbx_01',
      nama: 'BPOM (Per Varian) - Meet Me At The Altar EDP',
      harga_beli: 500000,
      harga_jual: 2500000,
      catatan: 'Registrasi Notifikasi Kosmetika BPOM RI',
      created_date: now,
      updated_date: now
    }
  ];
  localStorage.setItem(`${SANDBOX_STORAGE_PREFIX}Legalitas`, JSON.stringify(sampleLegalitas));

  const sampleJasa = [
    {
      id: 'jasa_sbx_01',
      nama: 'Jasa Ukuran ≤ 50ml (Racik, Filling, Crimp, Packaging)',
      ukuran_min_ml: 31,
      ukuran_max_ml: 50,
      harga_beli: 8000,
      harga_jual: 15000,
      catatan: 'Medium bottle perfume filling, crimping spray, sealing hardbox',
      created_date: now,
      updated_date: now
    }
  ];
  localStorage.setItem(`${SANDBOX_STORAGE_PREFIX}Jasa`, JSON.stringify(sampleJasa));

  // 9. Racikan Formula Database
  const sampleRacikan = [
    {
      id: 'formula_sbx_meet_01',
      nama_brand: 'MEET ME AT THE ALTAR',
      nama_varian: 'Eau De Parfum Midnight Reverie 50ml',
      kategori: 'Parfum',
      status: 'disetujui',
      volume_ml: 50,
      hpp_racikan: 18500,
      harga_jual: 45000,
      total_hpp: 32500,
      biaya_kemasan: 14000,
      biaya_operasional: 3000,
      komposisi: [
        { nama_bahan: 'Fragrance Oil Meet Me At The Altar (French Grade A+)', persentase: 20, volume_ml: 10 },
        { nama_bahan: 'Ethanol Absolute 96% Perfume Grade', persentase: 80, volume_ml: 40 }
      ],
      created_date: now,
      updated_date: now
    },
    {
      id: 'formula_sbx_meet_02',
      nama_brand: 'MEET ME AT THE ALTAR',
      nama_varian: 'Eau De Parfum Golden Hour 50ml',
      kategori: 'Parfum',
      status: 'disetujui',
      volume_ml: 50,
      hpp_racikan: 19000,
      harga_jual: 45000,
      total_hpp: 33000,
      biaya_kemasan: 14000,
      biaya_operasional: 3000,
      komposisi: [
        { nama_bahan: 'Fragrance Oil Meet Me At The Altar (French Grade A+)', persentase: 22, volume_ml: 11 },
        { nama_bahan: 'Ethanol Absolute 96% Perfume Grade', persentase: 78, volume_ml: 39 }
      ],
      created_date: now,
      updated_date: now
    },
    {
      id: 'formula_sbx_01',
      nama_brand: 'Glow Botanica Lab',
      nama_varian: 'Brightening Niacinamide Serum 20ml',
      kategori: 'Skincare',
      status: 'disetujui',
      volume_ml: 20,
      hpp_racikan: 8500,
      harga_jual: 25000,
      total_hpp: 12500,
      biaya_kemasan: 3000,
      biaya_operasional: 1000,
      created_date: now,
      updated_date: now
    }
  ];
  localStorage.setItem(`${SANDBOX_STORAGE_PREFIX}RacikanDatabase`, JSON.stringify(sampleRacikan));

  // Shared components list for MEET ME AT THE ALTAR PO & Invoice
  const meetMeComponents = [
    {
      category: 'botol',
      inventory_item_id: 'botol-botol_sbx_01',
      item_name: 'Botol Kaca Parfum 50ml Square Clear Luxe',
      vendor: 'PT Packaging Prima Indonesia',
      qty_needed: 500,
      stock_available: 2500,
      unit: 'pcs',
      status: 'ready',
      is_integrated: true
    },
    {
      category: 'tutup',
      inventory_item_id: 'tutup-tutup_sbx_01',
      item_name: 'Cap Tutup Gold Metal Magnetic 50ml',
      vendor: 'PT Packaging Prima Indonesia',
      qty_needed: 500,
      stock_available: 2500,
      unit: 'pcs',
      status: 'ready',
      is_integrated: true
    },
    {
      category: 'spray',
      inventory_item_id: 'spray-spray_sbx_01',
      item_name: 'Crimp Spray Gold Mist FEA 15mm',
      vendor: 'PT Packaging Prima Indonesia',
      qty_needed: 500,
      stock_available: 2500,
      unit: 'pcs',
      status: 'ready',
      is_integrated: true
    },
    {
      category: 'bibit',
      inventory_item_id: 'bahan-bahan_sbx_01',
      item_name: 'Fragrance Oil Meet Me At The Altar (French Grade A+)',
      vendor: 'PT Chemika Prima Sejahtera',
      qty_needed: 7.5,
      stock_available: 50,
      unit: 'kg',
      status: 'ready',
      is_integrated: true
    }
  ];

  const meetMeAddons = [
    {
      category: 'tambahan',
      inventory_item_id: 'tambahan-item_sbx_box_01',
      item_name: 'Hardbox Sliding Meet Me At The Altar 50ml',
      vendor: 'Percetakan Kemasan Nusantara',
      qty_needed: 500,
      stock_available: 2500,
      unit: 'pcs',
      status: 'ready',
      is_integrated: true
    },
    {
      category: 'tambahan',
      inventory_item_id: 'tambahan-item_sbx_stiker_01',
      item_name: 'Stiker Label Vinyl Gold Foil 50ml',
      vendor: 'Percetakan Kemasan Nusantara',
      qty_needed: 500,
      stock_available: 3000,
      unit: 'pcs',
      status: 'ready',
      is_integrated: true
    }
  ];

  const meetMeProducts = [
    {
      product_name: 'Eau De Parfum Midnight Reverie',
      variant_name: 'Eau De Parfum Midnight Reverie 50ml',
      nama_produk: 'Eau De Parfum Midnight Reverie',
      target_qty: 300,
      final_qty: 300,
      qty: 300,
      botol_ukuran_aktual: 50,
      volume_ml: 50,
      unit_price: 45000,
      total_price: 13500000,
      qc_data: { status: 'passed' }
    },
    {
      product_name: 'Eau De Parfum Golden Hour',
      variant_name: 'Eau De Parfum Golden Hour 50ml',
      nama_produk: 'Eau De Parfum Golden Hour',
      target_qty: 200,
      final_qty: 200,
      qty: 200,
      botol_ukuran_aktual: 50,
      volume_ml: 50,
      unit_price: 45000,
      total_price: 9000000,
      qc_data: { status: 'passed' }
    }
  ];

  const meetMeShipments = [
    {
      no_surat_jalan: 'SJ-MEET-001',
      shipped_at: now,
      shipping_via: 'Lalamove',
      driver_name: 'Budi Santoso (Lalamove)',
      vehicle_plate: 'B 1234 SAT',
      recipient_name: 'Fadli',
      recipient_phone: '081234567890',
      address: 'Jl. Pajajaran No. 88, Baranangsiang, Bogor Timur, Kota Bogor, Jawa Barat 16143',
      label_size: 'besar',
      tgl_produksi: now.split('T')[0],
      exp_date: new Date(Date.now() + 3 * 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      pcs_per_dus: 25,
      catatan_dus: 'Fragile - Jangan Dibanting',
      catatan_surat_jalan: 'Batch 1 Pengiriman Launching Meet Me At The Altar (2 Varian)',
      items: [
        {
          variant_name: 'Eau De Parfum Midnight Reverie 50ml',
          qty_dikirim: 300,
          qty_akhir: 300,
          satuan: 'pcs',
          pcs_per_dus: 25,
          catatan: 'Karton 01-12 (Varian Midnight Reverie)',
          catatan_label: 'Varian Midnight Reverie (Dus 01-12)'
        },
        {
          variant_name: 'Eau De Parfum Golden Hour 50ml',
          qty_dikirim: 200,
          qty_akhir: 200,
          satuan: 'pcs',
          pcs_per_dus: 25,
          catatan: 'Karton 13-20 (Varian Golden Hour)',
          catatan_label: 'Varian Golden Hour (Dus 13-20)'
        }
      ]
    }
  ];

  // 10. Production Order (PO Produksi)
  const sampleProductionOrders = [
    {
      id: 'PO-MEET-2026-001',
      order_number: 'PO-MEET-2026-001',
      brand_name: 'MEET ME AT THE ALTAR',
      customerName: 'CV Meet Me At The Altar Indonesia',
      customer_name: 'CV Meet Me At The Altar Indonesia',
      product_name: 'Eau De Parfum Meet Me At The Altar 50ml (2 Varian)',
      nama_produk: 'Eau De Parfum Meet Me At The Altar 50ml (2 Varian)',
      target_qty: 500,
      final_qty: 500,
      quantity: 500,
      total_pcs: 500,
      unit_price: 45000,
      total_amount: 22500000,
      grandTotal: 24975000,
      status: 'siap_kirim',
      statusProduksi: 'SIAP KIRIM',
      surat_jalan_number: 'SJ-MEET-001',
      shipping_address: 'Jl. Pajajaran No. 88, Baranangsiang, Bogor Timur, Kota Bogor, Jawa Barat 16143',
      alamat_pengiriman: 'Jl. Pajajaran No. 88, Baranangsiang, Bogor Timur, Kota Bogor, Jawa Barat 16143',
      recipient_name: 'Fadli',
      recipient_phone: '081234567890',
      shipping_via: 'Lalamove',
      courier: 'Lalamove Van',
      driver_name: 'Budi Santoso',
      vehicle_plate: 'B 1234 SAT',
      notes: 'Batch Launching 500 Pcs (2 Varian) - Siap Kirim & Cetak Surat Jalan / Label Dus',
      shipped_at: now,
      products: meetMeProducts,
      components: meetMeComponents,
      additional_components: meetMeAddons,
      shipments: meetMeShipments,
      created_date: now,
      updated_date: now
    },
    {
      id: 'PO-DEMO-001',
      order_number: 'PO-DEMO-001',
      brand_name: 'Glow Botanica Lab',
      customerName: 'PT Glow Botanica Indonesia',
      customer_name: 'PT Glow Botanica Indonesia',
      product_name: 'Brightening Niacinamide Serum 20ml',
      nama_produk: 'Brightening Niacinamide Serum 20ml',
      target_qty: 1000,
      final_qty: 1000,
      quantity: 1000,
      total_pcs: 1000,
      unit_price: 25000,
      total_amount: 25000000,
      grandTotal: 25000000,
      status: 'in_progress',
      statusProduksi: 'PROSES PRODUKSI',
      created_date: now,
      updated_date: now
    }
  ];
  localStorage.setItem(`${SANDBOX_STORAGE_PREFIX}ProductionOrder`, JSON.stringify(sampleProductionOrders));

  // 11. Client PO (Invoice Finance)
  const samplePO = [
    {
      id: 'PO-MEET-2026-001',
      order_number: 'PO-MEET-2026-001',
      invoice_number: 'INV-2026-MMA-001',
      brand_name: 'MEET ME AT THE ALTAR',
      client_id: 'client_sbx_meetme',
      customerName: 'CV Meet Me At The Altar Indonesia',
      customer_name: 'CV Meet Me At The Altar Indonesia',
      pic_brand: 'Fadli',
      pic: 'Fadli',
      phone: '081234567890',
      nama_produk: 'Eau De Parfum Meet Me At The Altar 50ml',
      quantity: 500,
      total_pcs: 500,
      unit_price: 45000,
      subtotal: 22500000,
      tax_amount: 2475000,
      total_amount: 24975000,
      grandTotal: 24975000,
      nominal_dp: 12487500,
      nominal_lunas: 12487500,
      sisa_tagihan: 0,
      payment_status: 'paid',
      status: 'siap_kirim',
      statusProduksi: 'SIAP KIRIM',
      alamat: 'Jl. Pajajaran No. 88, Baranangsiang, Bogor Timur, Kota Bogor, Jawa Barat 16143',
      shipping_address: 'Jl. Pajajaran No. 88, Baranangsiang, Bogor Timur, Kota Bogor, Jawa Barat 16143',
      surat_jalan_number: 'SJ-MEET-001',
      shipped_at: now,
      products: meetMeProducts,
      components: meetMeComponents,
      additional_components: meetMeAddons,
      shipments: meetMeShipments,
      created_date: now,
      updated_date: now
    },
    {
      id: 'PO-DEMO-001',
      order_number: 'PO-DEMO-001',
      invoice_number: 'INV-2026-GB-001',
      brand_name: 'Glow Botanica Lab',
      customerName: 'PT Glow Botanica Indonesia',
      nama_produk: 'Brightening Niacinamide Serum 20ml',
      quantity: 1000,
      total_pcs: 1000,
      unit_price: 25000,
      total_amount: 25000000,
      grandTotal: 25000000,
      status: 'pending',
      payment_status: 'unpaid',
      statusProduksi: 'PROSES PRODUKSI',
      created_date: now,
      updated_date: now
    }
  ];
  localStorage.setItem(`${SANDBOX_STORAGE_PREFIX}ClientPO`, JSON.stringify(samplePO));

  // 12. Supplier Database
  const sampleSuppliers = [
    {
      id: 'supp_sbx_01',
      nama_supplier: 'PT Packaging Prima Indonesia',
      kategori: 'Packaging Botol, Tutup & Spray',
      kontak: '081199887766',
      pic: 'Hendra Wijaya',
      alamat: 'Kawasan Industri Sentul, Bogor',
      created_date: now,
      updated_date: now
    },
    {
      id: 'supp_sbx_02',
      nama_supplier: 'PT Chemika Prima Sejahtera',
      kategori: 'Bahan Baku Fragrance & Pelarut',
      kontak: '081122334455',
      pic: 'Budi Santoso',
      alamat: 'Kawasan Industri Jababeka, Cikarang',
      created_date: now,
      updated_date: now
    },
    {
      id: 'supp_sbx_03',
      nama_supplier: 'Percetakan Kemasan Nusantara',
      kategori: 'Item Tambahan (Box & Stiker)',
      kontak: '081377889900',
      pic: 'Agus Gunawan',
      alamat: 'Jl. Rawa Gelam IV, Pulogadung, Jakarta Timur',
      created_date: now,
      updated_date: now
    }
  ];
  localStorage.setItem(`${SANDBOX_STORAGE_PREFIX}SupplierDatabase`, JSON.stringify(sampleSuppliers));

  if (shouldReload) {
    window.location.reload();
  }
};

export const ensureSandboxInitialized = () => {
  if (typeof window === 'undefined') return;
  if (!isTestingModeActive()) return;
  const existingBotol = localStorage.getItem(`${SANDBOX_STORAGE_PREFIX}Botol`);
  const existingPO = localStorage.getItem(`${SANDBOX_STORAGE_PREFIX}ProductionOrder`);
  if (!existingBotol || !existingPO) {
    seedSampleTestingData(false);
  }
};

